import { Vector3 } from 'three';
import { fbm, smoothstep, clamp, lerp } from '../utils/rng';

/**
 * Source de verite unique du relief.
 *
 * Maillage du sol, placement des cibles et du decor, assiette du char,
 * trajectoire des obus et point d'impact predit interrogent tous
 * `terrainHeight`. Aucun Raycaster : un tir doit pouvoir etre resolu
 * analytiquement, sans toucher a la scene.
 * cf. doc/02-terrain-et-bruit.md
 */

export const WORLD_SIZE = 760;

/** Limites de deplacement, plus serrees que le maillage pour ne jamais voir le bord. */
export const PLAY_BOUNDS = { minX: -150, maxX: 150, minZ: -182, maxZ: 150 };

/**
 * Ligne de tir. Le champ de tir est oriente selon +z : le char part d'ici et
 * les cibles sont en aval. Les deux chiffres servent au placement des cibles
 * comme a celui du char, pour qu'ils restent coherents si on bouge l'un.
 */
export const FIRING_LINE_Z = -152;
export const DOWNRANGE = { near: -74, far: 124 };

const SEED = 20261008;

/**
 * Merlons : longs bourrelets de terre en travers du champ de tir.
 *
 * Ils ne sont pas decoratifs. Un champ de tir parfaitement plat se joue en
 * tir tendu, toujours au meme angle, et la balistique ne se voit jamais. Avec
 * trois merlons, une partie des cibles n'est atteignable qu'en tir courbe :
 * c'est ce qui donne un sens a la hausse du canon.
 * cf. doc/04-balistique.md
 */
interface Berm {
  z: number;
  height: number;
  /** Demi-epaisseur du bourrelet, le long de l'axe de tir. */
  halfWidth: number;
  /** Centre et demi-largeur de la breche laissee pour passer. */
  gapX: number;
  gapHalf: number;
}

const BERMS: Berm[] = [
  { z: -72, height: 5.4, halfWidth: 9, gapX: 44, gapHalf: 14 },
  { z: 6, height: 7.6, halfWidth: 12, gapX: -50, gapHalf: 15 },
  { z: 92, height: 6.2, halfWidth: 10, gapX: 22, gapHalf: 13 },
];

/** Crateres, places a la main : ils habillent le sol et justifient les traces. */
const CRATERS: { x: number; z: number; r: number; depth: number }[] = [
  { x: -28, z: -108, r: 9, depth: 1.5 },
  { x: 36, z: -46, r: 12, depth: 2.1 },
  { x: -64, z: -18, r: 8, depth: 1.3 },
  { x: 14, z: 44, r: 14, depth: 2.4 },
  { x: 82, z: 70, r: 10, depth: 1.7 },
  { x: -92, z: 96, r: 11, depth: 1.9 },
  { x: -6, z: 116, r: 9, depth: 1.4 },
];

/** Axe du lit asseche : il serpente en travers du champ. */
export function washCenterX(z: number): number {
  return 46 * Math.sin(z * 0.0082 + 0.7) + 14 * Math.sin(z * 0.031 - 1.1);
}

/** exp(-u^4) : un plateau franc plutot qu'une gaussienne molle. */
function plateau(u: number): number {
  const u2 = u * u;
  return Math.exp(-u2 * u2);
}

function bermHeight(x: number, z: number): number {
  let h = 0;
  for (const b of BERMS) {
    const along = plateau((z - b.z) / b.halfWidth);
    if (along < 0.002) continue;
    // La breche : un creux dans le bourrelet, pas une interruption nette.
    const gap = 1 - plateau((x - b.gapX) / b.gapHalf);
    // Les extremites se fondent dans les collines, sinon le merlon s'arrete
    // en plein air sur un bord vertical.
    const ends = 1 - smoothstep(118, 150, Math.abs(x));
    h += b.height * along * gap * ends;
  }
  return h;
}

function craterHeight(x: number, z: number): number {
  let h = 0;
  for (const c of CRATERS) {
    const u = Math.hypot(x - c.x, z - c.z) / c.r;
    if (u > 1.6) continue;
    // Bol creuse jusqu'a u=1, puis bourrelet d'ejecta autour de la levre.
    if (u < 1) h -= c.depth * (1 - u * u);
    const rim = (u - 1.0) / 0.3;
    h += c.depth * 0.42 * Math.exp(-rim * rim);
  }
  return h;
}

/**
 * Distance au centre du bassin, legerement allongee selon l'axe de tir.
 *
 * Exportee parce que la *couleur* du terrain en depend autant que son
 * altitude : les amenagements du champ de tir (lit asseche, merlons) doivent
 * s'arreter aux memes bornes dans les deux, sinon on peint une trainee de
 * poussiere a flanc de colline.
 */
export function basinRadius(x: number, z: number): number {
  return Math.hypot(x, (z + 16) * 0.92);
}

export function terrainHeight(x: number, z: number): number {
  const r = basinRadius(x, z);

  // 1. Les collines qui ferment le champ de tir, puis le massif derriere.
  //
  // Elles sont basses et *loin*. Un premier reglage les mettait a 126 m de
  // haut a partir de r = 196 : depuis la ligne de tir elles montaient a 25
  // degres au-dessus de l'horizon et bouchaient le ciel entier. Or le ciel
  // n'est pas decoratif ici -- c'est le fond sur lequel se detache une cible
  // a 250 m, et sans lui on ne voit plus rien. 95 m a partir de r = 225 les
  // ramene a une dizaine de degres.
  let h = Math.pow(smoothstep(225, 370, r), 1.15) * 95;
  h += smoothstep(340, 480, r) * 85;

  // 2. Plaine ondulante : trois echelles. La plus courte reste au-dessus du
  //    pas du maillage (2 unites), sinon on ne produit que de l'aliasing.
  h += fbm(x * 0.0042, z * 0.0042, 4, SEED) * 8.5;
  h += fbm(x * 0.019, z * 0.019, 3, SEED + 71) * 2.0;
  h += fbm(x * 0.082, z * 0.082, 2, SEED + 313) * 0.42;

  // 3. Lit asseche, creuse uniquement dans la cuvette.
  const d = (x - washCenterX(z)) / 15;
  h -= 2.6 * Math.exp(-d * d) * (1 - smoothstep(190, 250, r));

  // 4. Amenagements du champ de tir. Ils s'effacent dans les collines.
  const basin = 1 - smoothstep(170, 228, r);
  h += (bermHeight(x, z) + craterHeight(x, z)) * basin;

  // 5. Plateforme de tir : on aplanit franchement autour du point de depart,
  //    sinon le char demarre penche et le premier tir part de travers.
  const pad = plateau(Math.hypot(x, (z - FIRING_LINE_Z) * 0.8) / 26);
  return lerp(h, 2.2, pad * 0.85);
}

const EPS = 0.7;

/** Normale par differences finies : assiette du char, orientation des traces. */
export function terrainNormal(x: number, z: number, out = new Vector3()): Vector3 {
  const hL = terrainHeight(x - EPS, z);
  const hR = terrainHeight(x + EPS, z);
  const hD = terrainHeight(x, z - EPS);
  const hU = terrainHeight(x, z + EPS);
  return out.set(hL - hR, 2 * EPS, hD - hU).normalize();
}

/** 0 = plat, 1 = vertical. */
export function terrainSlope(x: number, z: number): number {
  return 1 - terrainNormal(x, z).y;
}

export function insideBounds(x: number, z: number): boolean {
  return (
    x > PLAY_BOUNDS.minX && x < PLAY_BOUNDS.maxX && z > PLAY_BOUNDS.minZ && z < PLAY_BOUNDS.maxZ
  );
}

/**
 * Cherche un point de depart plat sur la plateforme de tir plutot que de
 * coder un couple (x, z) en dur qui casserait au moindre reglage du relief.
 */
export function findSpawn(): Vector3 {
  const best = new Vector3(0, 0, FIRING_LINE_Z);
  for (let i = 0; i < 240; i++) {
    // Spirale serree autour du centre de la plateforme.
    const a = i * 2.39996;
    const rad = Math.sqrt(i) * 1.1;
    const x = Math.cos(a) * rad;
    const z = FIRING_LINE_Z + Math.sin(a) * rad;
    if (terrainSlope(x, z) < 0.035 && insideBounds(x, z)) {
      return best.set(x, terrainHeight(x, z), z);
    }
  }
  best.y = terrainHeight(best.x, best.z);
  return best;
}

/** Altitude du sol, bornee au champ de tir : utile pour les caméras. */
export function groundAt(x: number, z: number): number {
  return terrainHeight(
    clamp(x, -WORLD_SIZE / 2, WORLD_SIZE / 2),
    clamp(z, -WORLD_SIZE / 2, WORLD_SIZE / 2),
  );
}
